/**
 * FERP Portal - Queue Manager Component (Admin View)
 *
 * LIFE-CRITICAL: Queue management is essential for ensuring
 * timely processing of workflows, especially time-sensitive
 * healthcare and financial operations.
 *
 * Queue Types:
 * - TASK: Standard task processing queues
 * - PRIORITY: High-priority items requiring immediate attention
 * - DEAD_LETTER: Failed items requiring manual intervention
 * - SCHEDULED: Time-delayed execution queues
 * - SAGA: Distributed transaction coordination queues
 */

import React, { useState, useMemo } from 'react';
import {
  Card,
  Group,
  Stack,
  Badge,
  Button,
  TextInput,
  Select,
  Table,
  ActionIcon,
  Menu,
  Modal,
  Tabs,
  SimpleGrid,
  ThemeIcon,
  Progress,
  Paper,
  Text,
  Title,
  RingProgress,
  Tooltip,
  Alert,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconList,
  IconSearch,
  IconFilter,
  IconDotsVertical,
  IconEye,
  IconRefresh,
  IconPlayerPlay,
  IconPlayerPause,
  IconTrash,
  IconAlertTriangle,
  IconClock,
  IconFlame,
  IconSkull,
  IconCalendarTime,
  IconArrowsExchange,
  IconReload,
  IconArchive,
} from '@tabler/icons-react';
import {
  SafetyLevel,
  SAFETY_LEVEL_UI_CONFIG,
  getLifeCriticalBorderStyle,
} from '@/config/safety-levels';
import {
  QUEUE_TYPE_UI_CONFIG as TYPE_CONFIG,
  QUEUE_STATUS_UI_CONFIG as STATUS_CONFIG,
  type QueueType,
  type QueueStatus,
} from '@/config/queue-types';

// Queue status constants (matches QueueStatus type from queue-types config)
const QueueStatusValue = {
  ACTIVE: 'ACTIVE' as QueueStatus,
  PAUSED: 'PAUSED' as QueueStatus,
} as const;

// Consumer status constants (matches QueueConsumer.status type)
const ConsumerStatus = {
  ACTIVE: 'ACTIVE',
  IDLE: 'IDLE',
  BUSY: 'BUSY',
} as const;
// NOTE: useListQueuesQuery not yet implemented in backend GraphQL schema
// TODO: Add GraphQL query for queues when backend implements it
// import { useListQueuesQuery } from '@/generated/graphql';

interface QueueMetrics {
  messagesInQueue: number;
  messagesProcessing: number;
  messagesCompleted: number;
  messagesFailed: number;
  avgProcessingTime: number; // ms
  throughput: number; // messages/minute
  oldestMessageAge: number; // seconds
}

interface QueueConsumer {
  id: string;
  name: string;
  status: 'ACTIVE' | 'IDLE' | 'BUSY';
  messagesProcessed: number;
  lastActivityAt: string;
}

interface QueueItem {
  id: string;
  payload: Record<string, unknown>;
  priority: number;
  attempts: number;
  maxAttempts: number;
  createdAt: string;
  scheduledFor?: string;
  lastError?: string;
}

interface Queue {
  id: string;
  name: string;
  description: string;
  type: QueueType;
  status: QueueStatus;
  safetyLevel: SafetyLevel;
  metrics: QueueMetrics;
  consumers: QueueConsumer[];
  recentItems: QueueItem[];
  maxSize: number;
  retryPolicy: {
    maxAttempts: number;
    backoffMultiplier: number;
    initialDelay: number;
  };
  alertThresholds: {
    queueDepth: number;
    processingTime: number;
    errorRate: number;
  };
  createdAt: string;
  tenantId: string;
}

// Mock data - replace with GraphQL query when schema is ready
const MOCK_QUEUES: Queue[] = [
  {
    id: 'queue_001',
    name: 'medication-administration',
    description: 'Critical medication administration tasks requiring immediate processing',
    type: 'PRIORITY',
    status: 'ACTIVE',
    safetyLevel: SafetyLevel.LIFE_CRITICAL,
    metrics: {
      messagesInQueue: 12,
      messagesProcessing: 3,
      messagesCompleted: 1847,
      messagesFailed: 2,
      avgProcessingTime: 1250,
      throughput: 45,
      oldestMessageAge: 180,
    },
    consumers: [
      { id: 'c1', name: 'med-worker-1', status: 'BUSY', messagesProcessed: 612, lastActivityAt: new Date(Date.now() - 1000 * 5).toISOString() },
      { id: 'c2', name: 'med-worker-2', status: 'BUSY', messagesProcessed: 598, lastActivityAt: new Date(Date.now() - 1000 * 3).toISOString() },
      { id: 'c3', name: 'med-worker-3', status: 'ACTIVE', messagesProcessed: 637, lastActivityAt: new Date(Date.now() - 1000 * 15).toISOString() },
    ],
    recentItems: [
      { id: 'item_001', payload: { patientId: 'P-4521', medicationId: 'MED-001' }, priority: 10, attempts: 0, maxAttempts: 3, createdAt: new Date(Date.now() - 1000 * 180).toISOString() },
      { id: 'item_002', payload: { patientId: 'P-4522', medicationId: 'MED-002' }, priority: 8, attempts: 0, maxAttempts: 3, createdAt: new Date(Date.now() - 1000 * 120).toISOString() },
    ],
    maxSize: 1000,
    retryPolicy: { maxAttempts: 3, backoffMultiplier: 2, initialDelay: 1000 },
    alertThresholds: { queueDepth: 50, processingTime: 5000, errorRate: 1 },
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 90).toISOString(),
    tenantId: 'tenant_001',
  },
  {
    id: 'queue_002',
    name: 'financial-approvals',
    description: 'Dual approval workflow for high-value financial transactions',
    type: 'TASK',
    status: 'ACTIVE',
    safetyLevel: SafetyLevel.CRITICAL,
    metrics: {
      messagesInQueue: 7,
      messagesProcessing: 2,
      messagesCompleted: 3421,
      messagesFailed: 5,
      avgProcessingTime: 45000,
      throughput: 12,
      oldestMessageAge: 3600,
    },
    consumers: [
      { id: 'c4', name: 'approval-worker-1', status: 'BUSY', messagesProcessed: 1712, lastActivityAt: new Date(Date.now() - 1000 * 30).toISOString() },
      { id: 'c5', name: 'approval-worker-2', status: 'IDLE', messagesProcessed: 1709, lastActivityAt: new Date(Date.now() - 1000 * 120).toISOString() },
    ],
    recentItems: [
      { id: 'item_003', payload: { transactionId: 'TXN-125000', amount: 125000 }, priority: 5, attempts: 0, maxAttempts: 3, createdAt: new Date(Date.now() - 1000 * 3600).toISOString() },
    ],
    maxSize: 500,
    retryPolicy: { maxAttempts: 3, backoffMultiplier: 2, initialDelay: 5000 },
    alertThresholds: { queueDepth: 25, processingTime: 120000, errorRate: 2 },
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 120).toISOString(),
    tenantId: 'tenant_001',
  },
  {
    id: 'queue_003',
    name: 'compliance-checks',
    description: 'Automated compliance validation checks',
    type: 'TASK',
    status: 'ACTIVE',
    safetyLevel: SafetyLevel.ELEVATED,
    metrics: {
      messagesInQueue: 23,
      messagesProcessing: 5,
      messagesCompleted: 8934,
      messagesFailed: 12,
      avgProcessingTime: 8500,
      throughput: 28,
      oldestMessageAge: 900,
    },
    consumers: [
      { id: 'c6', name: 'compliance-worker-1', status: 'BUSY', messagesProcessed: 2978, lastActivityAt: new Date(Date.now() - 1000 * 2).toISOString() },
      { id: 'c7', name: 'compliance-worker-2', status: 'BUSY', messagesProcessed: 2989, lastActivityAt: new Date(Date.now() - 1000 * 4).toISOString() },
      { id: 'c8', name: 'compliance-worker-3', status: 'BUSY', messagesProcessed: 2967, lastActivityAt: new Date(Date.now() - 1000 * 1).toISOString() },
    ],
    recentItems: [],
    maxSize: 2000,
    retryPolicy: { maxAttempts: 5, backoffMultiplier: 1.5, initialDelay: 2000 },
    alertThresholds: { queueDepth: 100, processingTime: 30000, errorRate: 5 },
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 60).toISOString(),
    tenantId: 'tenant_001',
  },
  {
    id: 'queue_004',
    name: 'medication-dlq',
    description: 'Dead letter queue for failed medication administration tasks',
    type: 'DEAD_LETTER',
    status: 'ACTIVE',
    safetyLevel: SafetyLevel.LIFE_CRITICAL,
    metrics: {
      messagesInQueue: 2,
      messagesProcessing: 0,
      messagesCompleted: 0,
      messagesFailed: 0,
      avgProcessingTime: 0,
      throughput: 0,
      oldestMessageAge: 7200,
    },
    consumers: [],
    recentItems: [
      { id: 'item_004', payload: { patientId: 'P-4510', medicationId: 'MED-003' }, priority: 10, attempts: 3, maxAttempts: 3, createdAt: new Date(Date.now() - 1000 * 7200).toISOString(), lastError: 'Timeout: Nurse station unresponsive' },
      { id: 'item_005', payload: { patientId: 'P-4515', medicationId: 'MED-004' }, priority: 9, attempts: 3, maxAttempts: 3, createdAt: new Date(Date.now() - 1000 * 3600).toISOString(), lastError: 'Medication scanner offline' },
    ],
    maxSize: 100,
    retryPolicy: { maxAttempts: 1, backoffMultiplier: 1, initialDelay: 0 },
    alertThresholds: { queueDepth: 5, processingTime: 0, errorRate: 0 },
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 90).toISOString(),
    tenantId: 'tenant_001',
  },
  {
    id: 'queue_005',
    name: 'scheduled-reports',
    description: 'Scheduled report generation tasks',
    type: 'SCHEDULED',
    status: 'ACTIVE',
    safetyLevel: SafetyLevel.STANDARD,
    metrics: {
      messagesInQueue: 15,
      messagesProcessing: 1,
      messagesCompleted: 2456,
      messagesFailed: 8,
      avgProcessingTime: 120000,
      throughput: 5,
      oldestMessageAge: 0,
    },
    consumers: [
      { id: 'c9', name: 'report-worker-1', status: 'BUSY', messagesProcessed: 1228, lastActivityAt: new Date(Date.now() - 1000 * 60).toISOString() },
      { id: 'c10', name: 'report-worker-2', status: 'IDLE', messagesProcessed: 1228, lastActivityAt: new Date(Date.now() - 1000 * 300).toISOString() },
    ],
    recentItems: [
      { id: 'item_006', payload: { reportType: 'DAILY_PERFORMANCE' }, priority: 3, attempts: 0, maxAttempts: 3, createdAt: new Date().toISOString(), scheduledFor: new Date(Date.now() + 1000 * 60 * 60 * 2).toISOString() },
    ],
    maxSize: 500,
    retryPolicy: { maxAttempts: 3, backoffMultiplier: 2, initialDelay: 60000 },
    alertThresholds: { queueDepth: 50, processingTime: 300000, errorRate: 10 },
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 45).toISOString(),
    tenantId: 'tenant_001',
  },
  {
    id: 'queue_006',
    name: 'saga-coordinator',
    description: 'Distributed transaction saga coordination',
    type: 'SAGA',
    status: 'ACTIVE',
    safetyLevel: SafetyLevel.CRITICAL,
    metrics: {
      messagesInQueue: 4,
      messagesProcessing: 2,
      messagesCompleted: 5678,
      messagesFailed: 23,
      avgProcessingTime: 35000,
      throughput: 18,
      oldestMessageAge: 450,
    },
    consumers: [
      { id: 'c11', name: 'saga-coordinator-1', status: 'BUSY', messagesProcessed: 2839, lastActivityAt: new Date(Date.now() - 1000 * 10).toISOString() },
      { id: 'c12', name: 'saga-coordinator-2', status: 'BUSY', messagesProcessed: 2839, lastActivityAt: new Date(Date.now() - 1000 * 8).toISOString() },
    ],
    recentItems: [],
    maxSize: 1000,
    retryPolicy: { maxAttempts: 5, backoffMultiplier: 2, initialDelay: 5000 },
    alertThresholds: { queueDepth: 50, processingTime: 60000, errorRate: 3 },
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 75).toISOString(),
    tenantId: 'tenant_001',
  },
  {
    id: 'queue_007',
    name: 'notifications',
    description: 'System notification delivery queue',
    type: 'TASK',
    status: 'PAUSED',
    safetyLevel: SafetyLevel.STANDARD,
    metrics: {
      messagesInQueue: 156,
      messagesProcessing: 0,
      messagesCompleted: 45678,
      messagesFailed: 234,
      avgProcessingTime: 500,
      throughput: 0,
      oldestMessageAge: 1800,
    },
    consumers: [
      { id: 'c13', name: 'notification-worker-1', status: 'IDLE', messagesProcessed: 15226, lastActivityAt: new Date(Date.now() - 1000 * 1800).toISOString() },
      { id: 'c14', name: 'notification-worker-2', status: 'IDLE', messagesProcessed: 15226, lastActivityAt: new Date(Date.now() - 1000 * 1800).toISOString() },
      { id: 'c15', name: 'notification-worker-3', status: 'IDLE', messagesProcessed: 15226, lastActivityAt: new Date(Date.now() - 1000 * 1800).toISOString() },
    ],
    recentItems: [],
    maxSize: 10000,
    retryPolicy: { maxAttempts: 3, backoffMultiplier: 2, initialDelay: 1000 },
    alertThresholds: { queueDepth: 500, processingTime: 5000, errorRate: 5 },
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 100).toISOString(),
    tenantId: 'tenant_001',
  },
];

export function QueueManager(): React.ReactElement {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [safetyFilter, setSafetyFilter] = useState<string | null>(null);
  const [selectedQueue, setSelectedQueue] = useState<Queue | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // NOTE: GraphQL query for queues not yet implemented in backend
  // Using mock data for now. When backend implements listQueues query,
  // uncomment the useListQueuesQuery hook and update this component.
  const loading = false;
  // Using state to allow proper type narrowing
  const [error] = useState<{ message: string } | null>(null);
  const refetch = () => Promise.resolve();

  // Use mock data since GraphQL query doesn't exist yet
  const queues = useMemo(() => {
    // TODO: When backend implements listQueues, transform GraphQL data here
    // For now, return mock data for development
    return MOCK_QUEUES;
  }, []);

  const filteredQueues = useMemo(() => {
    return queues.filter((queue) => {
      const matchesSearch =
        searchQuery === '' ||
        queue.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        queue.description.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType = !typeFilter || queue.type === typeFilter;
      const matchesStatus = !statusFilter || queue.status === statusFilter;
      const matchesSafety = !safetyFilter || queue.safetyLevel === safetyFilter;

      return matchesSearch && matchesType && matchesStatus && matchesSafety;
    });
  }, [queues, searchQuery, typeFilter, statusFilter, safetyFilter]);

  // Calculate overall statistics
  const stats = useMemo(() => {
    const totalQueues = queues.length;
    const totalMessages = queues.reduce((sum, q) => sum + q.metrics.messagesInQueue, 0);
    const totalProcessing = queues.reduce((sum, q) => sum + q.metrics.messagesProcessing, 0);
    const deadLetterCount = queues
      .filter((q) => q.type === 'DEAD_LETTER')
      .reduce((sum, q) => sum + q.metrics.messagesInQueue, 0);
    const lifeCriticalQueues = queues.filter((q) => q.safetyLevel === SafetyLevel.LIFE_CRITICAL).length;
    const pausedQueues = queues.filter((q) => q.status === QueueStatusValue.PAUSED).length;
    return { totalQueues, totalMessages, totalProcessing, deadLetterCount, lifeCriticalQueues, pausedQueues };
  }, [queues]);

  const openDetailModal = (queue: Queue) => {
    setSelectedQueue(queue);
    setDetailModalOpen(true);
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  const formatProcessingTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const getTypeIcon = (type: QueueType) => {
    const IconComponent = TYPE_CONFIG[type].icon;
    return <IconComponent size={16} />;
  };

  const getQueueHealthColor = (queue: Queue) => {
    const { metrics, alertThresholds } = queue;
    if (metrics.messagesInQueue > alertThresholds.queueDepth) return 'red';
    if (metrics.avgProcessingTime > alertThresholds.processingTime) return 'orange';
    if (metrics.messagesFailed / (metrics.messagesCompleted || 1) * 100 > alertThresholds.errorRate) return 'yellow';
    return 'green';
  };

  const getQueueUtilization = (queue: Queue) => {
    return Math.min((queue.metrics.messagesInQueue / queue.maxSize) * 100, 100);
  };

  // Handle loading state
  if (loading) {
    return (
      <Center h={400}>
        <Stack ta="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">Loading queues...</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between" ta="left">
        <div>
          <Title order={2}>Queue Manager</Title>
          <Text c="dimmed" size="sm">
            Real-time queue monitoring and management
          </Text>
        </div>
        <Group>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={() => refetch()}
            loading={loading}
          >
            Refresh All
          </Button>
        </Group>
      </Group>

      {/* Error Alert */}
      {error !== null && (
        <Alert
          icon={<IconAlertTriangle size={20} />}
          title="Error Loading Queues"
          color="red"
          variant="filled"
        >
          {error.message}
        </Alert>
      )}

      {/* Dead Letter Queue Warning */}
      {stats.deadLetterCount > 0 && (
        <Alert
          icon={<IconSkull size={20} />}
          title="Dead Letter Queue Alert"
          color="red"
          variant="filled"
        >
          {stats.deadLetterCount} message{stats.deadLetterCount > 1 ? 's' : ''} in dead letter queue
          require manual intervention. These may include life-critical tasks.
        </Alert>
      )}

      {/* Statistics Cards */}
      <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }} spacing="md">
        <Card withBorder padding="md">
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Total Queues
              </Text>
              <Text size="xl" fw={700}>
                {stats.totalQueues}
              </Text>
            </div>
            <ThemeIcon size="lg" variant="light" color="blue">
              <IconList size={20} />
            </ThemeIcon>
          </Group>
        </Card>

        <Card withBorder padding="md">
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Messages Queued
              </Text>
              <Text size="xl" fw={700}>
                {stats.totalMessages}
              </Text>
            </div>
            <ThemeIcon size="lg" variant="light" color="cyan">
              <IconClock size={20} />
            </ThemeIcon>
          </Group>
        </Card>

        <Card withBorder padding="md">
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Processing
              </Text>
              <Text size="xl" fw={700} c="green">
                {stats.totalProcessing}
              </Text>
            </div>
            <ThemeIcon size="lg" variant="light" color="green">
              <IconPlayerPlay size={20} />
            </ThemeIcon>
          </Group>
        </Card>

        <Card withBorder padding="md" style={{ borderLeft: '3px solid var(--mantine-color-red-6)' }}>
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Dead Letter
              </Text>
              <Text size="xl" fw={700} c="red">
                {stats.deadLetterCount}
              </Text>
            </div>
            <ThemeIcon size="lg" variant="light" color="red">
              <IconSkull size={20} />
            </ThemeIcon>
          </Group>
        </Card>

        <Card withBorder padding="md" style={{ borderLeft: '3px solid var(--mantine-color-red-6)' }}>
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Life Critical
              </Text>
              <Text size="xl" fw={700} c="red">
                {stats.lifeCriticalQueues}
              </Text>
            </div>
            <ThemeIcon size="lg" variant="light" color="red">
              <IconFlame size={20} />
            </ThemeIcon>
          </Group>
        </Card>

        <Card withBorder padding="md">
          <Group justify="space-between">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Paused
              </Text>
              <Text size="xl" fw={700} c="yellow">
                {stats.pausedQueues}
              </Text>
            </div>
            <ThemeIcon size="lg" variant="light" color="yellow">
              <IconPlayerPause size={20} />
            </ThemeIcon>
          </Group>
        </Card>
      </SimpleGrid>

      {/* Filters */}
      <Card withBorder>
        <Group>
          <TextInput
            placeholder="Search queues..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Type"
            leftSection={<IconFilter size={16} />}
            data={Object.entries(TYPE_CONFIG).map(([key, config]) => ({
              value: key,
              label: config.label,
            }))}
            value={typeFilter}
            onChange={setTypeFilter}
            clearable
            w={160}
          />
          <Select
            placeholder="Status"
            data={Object.entries(STATUS_CONFIG).map(([key, config]) => ({
              value: key,
              label: config.label,
            }))}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            w={130}
          />
          <Select
            placeholder="Safety Level"
            data={Object.entries(SAFETY_LEVEL_UI_CONFIG).map(([key, config]) => ({
              value: key,
              label: config.label,
            }))}
            value={safetyFilter}
            onChange={setSafetyFilter}
            clearable
            w={150}
          />
        </Group>
      </Card>

      {/* Queues Table */}
      <Card withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Type</Table.Th>
              <Table.Th>Queue</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Health</Table.Th>
              <Table.Th>Depth</Table.Th>
              <Table.Th>Throughput</Table.Th>
              <Table.Th>Avg Time</Table.Th>
              <Table.Th>Consumers</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredQueues.map((queue) => (
              <Table.Tr
                key={queue.id}
                style={getLifeCriticalBorderStyle(queue.safetyLevel)}
              >
                <Table.Td>
                  <ThemeIcon size="sm" variant="light" color={TYPE_CONFIG[queue.type].color}>
                    {getTypeIcon(queue.type)}
                  </ThemeIcon>
                </Table.Td>
                <Table.Td>
                  <Stack gap={2}>
                    <Group gap={4}>
                      <Text size="sm" fw={500}>
                        {queue.name}
                      </Text>
                      <Badge size="xs" color={SAFETY_LEVEL_UI_CONFIG[queue.safetyLevel].color}>
                        {SAFETY_LEVEL_UI_CONFIG[queue.safetyLevel].label}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {queue.description}
                    </Text>
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Badge color={STATUS_CONFIG[queue.status].color} variant="light">
                    {STATUS_CONFIG[queue.status].label}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Tooltip label={`Utilization: ${getQueueUtilization(queue).toFixed(1)}%`}>
                    <RingProgress
                      size={32}
                      thickness={3}
                      sections={[{ value: getQueueUtilization(queue), color: getQueueHealthColor(queue) }]}
                    />
                  </Tooltip>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <Text size="sm" fw={600}>
                      {queue.metrics.messagesInQueue}
                    </Text>
                    {queue.metrics.messagesProcessing > 0 && (
                      <Text size="xs" c="dimmed">
                        (+{queue.metrics.messagesProcessing})
                      </Text>
                    )}
                  </Group>
                  {queue.metrics.oldestMessageAge > 0 && (
                    <Text size="xs" c="dimmed">
                      oldest: {formatDuration(queue.metrics.oldestMessageAge)}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{queue.metrics.throughput}/min</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{formatProcessingTime(queue.metrics.avgProcessingTime)}</Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <Text size="sm">{queue.consumers.length}</Text>
                    <Text size="xs" c="dimmed">
                      ({queue.consumers.filter((c) => c.status === ConsumerStatus.BUSY).length} busy)
                    </Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Menu position="bottom-end" withinPortal>
                    <Menu.Target>
                      <ActionIcon variant="subtle">
                        <IconDotsVertical size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconEye size={14} />}
                        onClick={() => openDetailModal(queue)}
                      >
                        View Details
                      </Menu.Item>
                      {queue.status === QueueStatusValue.ACTIVE ? (
                        <Menu.Item leftSection={<IconPlayerPause size={14} />} color="yellow">
                          Pause Queue
                        </Menu.Item>
                      ) : (
                        <Menu.Item leftSection={<IconPlayerPlay size={14} />} color="green">
                          Resume Queue
                        </Menu.Item>
                      )}
                      <Menu.Item leftSection={<IconReload size={14} />}>Retry Failed</Menu.Item>
                      <Menu.Divider />
                      {queue.type === 'DEAD_LETTER' && (
                        <Menu.Item leftSection={<IconArchive size={14} />}>Archive All</Menu.Item>
                      )}
                      <Menu.Item leftSection={<IconTrash size={14} />} color="red">
                        Purge Queue
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>

      {/* Queue Detail Modal */}
      <Modal
        opened={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        title={
          <Group>
            {selectedQueue && (
              <ThemeIcon size="md" variant="light" color={TYPE_CONFIG[selectedQueue.type].color}>
                {getTypeIcon(selectedQueue.type)}
              </ThemeIcon>
            )}
            <div>
              <Text fw={600}>{selectedQueue?.name}</Text>
              <Text size="xs" c="dimmed">
                {selectedQueue && TYPE_CONFIG[selectedQueue.type].label}
              </Text>
            </div>
          </Group>
        }
        size="lg"
      >
        {selectedQueue && (
          <Tabs defaultValue="overview">
            <Tabs.List>
              <Tabs.Tab value="overview">Overview</Tabs.Tab>
              <Tabs.Tab value="consumers">Consumers ({selectedQueue.consumers.length})</Tabs.Tab>
              <Tabs.Tab value="items">Recent Items ({selectedQueue.recentItems.length})</Tabs.Tab>
              <Tabs.Tab value="config">Configuration</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="overview" pt="md">
              <Stack gap="md">
                <Text>{selectedQueue.description}</Text>

                <SimpleGrid cols={2}>
                  <Paper withBorder p="md" radius="md">
                    <Text size="xs" c="dimmed" tt="uppercase" mb="xs">
                      Queue Depth
                    </Text>
                    <Group ta="left">
                      <Text size="xl" fw={700}>
                        {selectedQueue.metrics.messagesInQueue}
                      </Text>
                      <Text size="sm" c="dimmed">
                        / {selectedQueue.maxSize}
                      </Text>
                    </Group>
                    <Progress
                      value={getQueueUtilization(selectedQueue)}
                      color={getQueueHealthColor(selectedQueue)}
                      size="sm"
                      mt="xs"
                    />
                  </Paper>

                  <Paper withBorder p="md" radius="md">
                    <Text size="xs" c="dimmed" tt="uppercase" mb="xs">
                      Throughput
                    </Text>
                    <Group ta="left">
                      <Text size="xl" fw={700}>
                        {selectedQueue.metrics.throughput}
                      </Text>
                      <Text size="sm" c="dimmed">
                        messages/min
                      </Text>
                    </Group>
                  </Paper>
                </SimpleGrid>

                <SimpleGrid cols={4}>
                  <Paper withBorder p="sm" radius="md">
                    <Text size="xs" c="dimmed">
                      Processing
                    </Text>
                    <Text size="lg" fw={600} c="blue">
                      {selectedQueue.metrics.messagesProcessing}
                    </Text>
                  </Paper>
                  <Paper withBorder p="sm" radius="md">
                    <Text size="xs" c="dimmed">
                      Completed
                    </Text>
                    <Text size="lg" fw={600} c="green">
                      {selectedQueue.metrics.messagesCompleted.toLocaleString()}
                    </Text>
                  </Paper>
                  <Paper withBorder p="sm" radius="md">
                    <Text size="xs" c="dimmed">
                      Failed
                    </Text>
                    <Text size="lg" fw={600} c="red">
                      {selectedQueue.metrics.messagesFailed}
                    </Text>
                  </Paper>
                  <Paper withBorder p="sm" radius="md">
                    <Text size="xs" c="dimmed">
                      Avg Time
                    </Text>
                    <Text size="lg" fw={600}>
                      {formatProcessingTime(selectedQueue.metrics.avgProcessingTime)}
                    </Text>
                  </Paper>
                </SimpleGrid>

                <SimpleGrid cols={2}>
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase">
                      Status
                    </Text>
                    <Badge color={STATUS_CONFIG[selectedQueue.status].color}>
                      {STATUS_CONFIG[selectedQueue.status].label}
                    </Badge>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase">
                      Safety Level
                    </Text>
                    <Badge color={SAFETY_LEVEL_UI_CONFIG[selectedQueue.safetyLevel].color}>
                      {SAFETY_LEVEL_UI_CONFIG[selectedQueue.safetyLevel].label}
                    </Badge>
                  </div>
                </SimpleGrid>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="consumers" pt="md">
              <Stack gap="md">
                {selectedQueue.consumers.length > 0 ? (
                  selectedQueue.consumers.map((consumer) => (
                    <Paper key={consumer.id} withBorder p="md" radius="md">
                      <Group justify="space-between">
                        <div>
                          <Text size="sm" fw={600}>
                            {consumer.name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            Processed: {consumer.messagesProcessed.toLocaleString()} messages
                          </Text>
                        </div>
                        <Badge
                          color={
                            consumer.status === ConsumerStatus.BUSY
                              ? 'green'
                              : consumer.status === ConsumerStatus.ACTIVE
                                ? 'blue'
                                : 'gray'
                          }
                        >
                          {consumer.status}
                        </Badge>
                      </Group>
                    </Paper>
                  ))
                ) : (
                  <Text c="dimmed" ta="center" py="xl">
                    No consumers attached to this queue.
                  </Text>
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="items" pt="md">
              <Stack gap="md">
                {selectedQueue.recentItems.length > 0 ? (
                  selectedQueue.recentItems.map((item) => (
                    <Paper key={item.id} withBorder p="md" radius="md">
                      <Group justify="space-between" mb="xs">
                        <Badge variant="outline" size="sm">
                          {item.id}
                        </Badge>
                        <Group gap={4}>
                          <Badge size="xs" color="blue">
                            Priority: {item.priority}
                          </Badge>
                          <Badge size="xs" color={item.attempts >= item.maxAttempts ? 'red' : 'gray'}>
                            Attempts: {item.attempts}/{item.maxAttempts}
                          </Badge>
                        </Group>
                      </Group>
                      <Text size="xs" c="dimmed" mb="xs">
                        Created: {new Date(item.createdAt).toLocaleString()}
                      </Text>
                      {item.scheduledFor && (
                        <Text size="xs" c="violet" mb="xs">
                          Scheduled: {new Date(item.scheduledFor).toLocaleString()}
                        </Text>
                      )}
                      {item.lastError && (
                        <Alert color="red" variant="light" icon={<IconAlertTriangle size={14} />} p="xs">
                          <Text size="xs">{item.lastError}</Text>
                        </Alert>
                      )}
                    </Paper>
                  ))
                ) : (
                  <Text c="dimmed" ta="center" py="xl">
                    No recent items in queue.
                  </Text>
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="config" pt="md">
              <Stack gap="md">
                <Paper withBorder p="md" radius="md">
                  <Text size="sm" fw={600} mb="xs">
                    Retry Policy
                  </Text>
                  <SimpleGrid cols={3}>
                    <div>
                      <Text size="xs" c="dimmed">
                        Max Attempts
                      </Text>
                      <Text size="sm">{selectedQueue.retryPolicy.maxAttempts}</Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">
                        Backoff Multiplier
                      </Text>
                      <Text size="sm">{selectedQueue.retryPolicy.backoffMultiplier}x</Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">
                        Initial Delay
                      </Text>
                      <Text size="sm">{selectedQueue.retryPolicy.initialDelay}ms</Text>
                    </div>
                  </SimpleGrid>
                </Paper>

                <Paper withBorder p="md" radius="md">
                  <Text size="sm" fw={600} mb="xs">
                    Alert Thresholds
                  </Text>
                  <SimpleGrid cols={3}>
                    <div>
                      <Text size="xs" c="dimmed">
                        Queue Depth
                      </Text>
                      <Text size="sm">{selectedQueue.alertThresholds.queueDepth} messages</Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">
                        Processing Time
                      </Text>
                      <Text size="sm">{formatProcessingTime(selectedQueue.alertThresholds.processingTime)}</Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">
                        Error Rate
                      </Text>
                      <Text size="sm">{selectedQueue.alertThresholds.errorRate}%</Text>
                    </div>
                  </SimpleGrid>
                </Paper>
              </Stack>
            </Tabs.Panel>
          </Tabs>
        )}
      </Modal>
    </Stack>
  );
}
